import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MoreVertical, Volume2, VolumeX, Mic, Square, BookOpen, ScanBarcode, Map } from "lucide-react";
import { useAudioControl } from "@/lib/audio-control-store";
import { useLocation } from "wouter";
import { useGetDashboardKpis } from "@workspace/api-client-react";
import { useBarcodeScanner } from "@/lib/barcode-scanner-store";
import { useTour } from "@/lib/product-tour";
import { format } from "date-fns";

function useContextualSummary(location: string, kpis: any): string | null {
  const { t } = useTranslation();
  if (location !== "/dashboard" && location !== "/") return null;
  if (!kpis) return null;
  const parts: string[] = [];
  parts.push(t("audio.dailyNutritionSummary"));
  if (kpis.caloriesTotal != null) parts.push(t("audio.consumedCalories", { count: Math.round(kpis.caloriesTotal) }));
  if (kpis.proteinTotal != null) parts.push(t("audio.proteinGrams", { count: Math.round(kpis.proteinTotal) }));
  if (kpis.carbsTotal != null) parts.push(t("audio.carbsGrams", { count: Math.round(kpis.carbsTotal) }));
  if (kpis.fatTotal != null) parts.push(t("audio.fatGrams", { count: Math.round(kpis.fatTotal) }));
  if (kpis.fiberTotal != null) parts.push(t("audio.fiberGrams", { count: Math.round(kpis.fiberTotal) }));
  if (kpis.adequateCount != null) parts.push(t("audio.adequateNutrients", { count: kpis.adequateCount }));
  if (kpis.deficientCount != null && kpis.deficientCount > 0) parts.push(t("audio.deficientNutrients", { count: kpis.deficientCount }));
  if (kpis.tefScore != null) parts.push(t("audio.tefScore", { score: Math.round(kpis.tefScore) }));
  if (kpis.gutIQ?.avgScore != null) parts.push(t("audio.gutIqScore", { score: kpis.gutIQ.avgScore }));
  return parts.join(" ");
}

export function AudioControlFAB() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { voiceEnabled, setVoiceEnabled, isSpeaking, speakText, stopSpeaking } = useAudioControl();
  const [location, setLocation] = useLocation();
  const { openScanner } = useBarcodeScanner();
  const { startTour, startPageTour, tourEnabled, setTourEnabled } = useTour();
  const [scanning, setScanning] = useState(false);

  const dateStr = format(new Date(), "yyyy-MM-dd");
  const { data: kpis } = useGetDashboardKpis({ date: dateStr, period: "day" as const });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (!open) return;
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const contextSummary = useContextualSummary(location, kpis);

  const handleReadAloud = () => {
    if (contextSummary) {
      speakText(contextSummary, true);
      setOpen(false);
    }
  };

  const handleBarcodeScan = async () => {
    if (scanning) return;
    setScanning(true);
    setOpen(false);
    try {
      const barcode = await openScanner();
      if (!barcode) return;
      if (location === "/plate-talk" || location.startsWith("/plate-talk?")) {
        window.dispatchEvent(new CustomEvent("barcode-scanned", { detail: barcode }));
      } else {
        setLocation(`/plate-talk?barcode=${encodeURIComponent(barcode)}`);
      }
    } finally {
      setScanning(false);
    }
  };

  const showSpeakingPulse = isSpeaking;

  return (
    <div data-tour="audio-fab" className="fixed fab-bottom-2 md:bottom-24 right-4 md:right-6 z-40">
      {open && (
        <div
          ref={popoverRef}
          className="absolute bottom-14 right-0 w-64 rounded-xl bg-background/95 backdrop-blur-md border border-border shadow-2xl overflow-hidden"
          style={{ boxShadow: "0 0 30px rgba(0,0,0,0.3), 0 0 15px rgba(var(--glow-rgb), 0.1)" }}
        >
          <div className="px-4 py-3 border-b border-border/50">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("audio.quickActions")}</p>
          </div>

          <div className="p-2 space-y-1">
            <button
              onClick={handleBarcodeScan}
              disabled={scanning}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-amber-500/10 transition-colors text-left disabled:opacity-50"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-amber-500/20 text-amber-400">
                <ScanBarcode className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{t("audio.scanBarcode")}</p>
                <p className="text-[10px] text-muted-foreground">{t("audio.scanBarcodeDesc")}</p>
              </div>
            </button>

            <div className="border-t border-border/30 my-1" />

            <button
              onClick={() => {
                const next = !voiceEnabled;
                setVoiceEnabled(next);
                if (!next) { stopSpeaking(); }
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left group"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${voiceEnabled ? "bg-cyan-500/20 text-cyan-400" : "bg-muted/30 text-muted-foreground"}`}>
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{t("audio.voiceResponses")}</p>
                <p className="text-[10px] text-muted-foreground">{t("audio.voiceResponsesDesc")}</p>
              </div>
              <div className={`w-9 h-5 rounded-full transition-colors relative ${voiceEnabled ? "bg-cyan-500" : "bg-muted/40"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${voiceEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
            </button>

            {isSpeaking && (
              <button
                onClick={() => { stopSpeaking(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-500/10 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-red-500/20 text-red-400">
                  <Square className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-400">{t("audio.stopSpeaking")}</p>
                  <p className="text-[10px] text-muted-foreground">{t("audio.audioPlaying")}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-0.5 bg-red-400 rounded-full animate-pulse" style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 150}ms` }} />
                  ))}
                </div>
              </button>
            )}

            {contextSummary && (
              <button
                onClick={handleReadAloud}
                disabled={isSpeaking}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/10 transition-colors text-left disabled:opacity-50"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-primary/20 text-primary">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{t("audio.readDashboardSummary")}</p>
                  <p className="text-[10px] text-muted-foreground">{t("audio.readDashboardSummaryDesc")}</p>
                </div>
              </button>
            )}

            <div className="border-t border-border/30 my-1" />

            <button
              onClick={() => {
                setOpen(false);
                setTimeout(() => startPageTour(location), 200);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-violet-500/10 transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-violet-500/20 text-violet-400">
                <Map className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{t("audio.tourThisPage")}</p>
                <p className="text-[10px] text-muted-foreground">{t("audio.tourThisPageDesc")}</p>
              </div>
            </button>

            <button
              onClick={() => {
                setOpen(false);
                setTimeout(() => startTour(), 200);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-violet-500/10 transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-violet-500/20 text-violet-400">
                <Map className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{t("audio.fullTourGuide")}</p>
                <p className="text-[10px] text-muted-foreground">{t("audio.fullTourGuideDesc")}</p>
              </div>
            </button>

            <button
              onClick={() => setTourEnabled(!tourEnabled)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left group"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${tourEnabled ? "bg-violet-500/20 text-violet-400" : "bg-muted/30 text-muted-foreground"}`}>
                <Map className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{t("audio.autoTour")}</p>
                <p className="text-[10px] text-muted-foreground">{t("audio.autoTourDesc")}</p>
              </div>
              <div className={`w-9 h-5 rounded-full transition-colors relative ${tourEnabled ? "bg-violet-500" : "bg-muted/40"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${tourEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
            </button>
          </div>

          {isSpeaking && (
            <div className="px-4 py-2 border-t border-border/50 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-[10px] text-muted-foreground">
                {t("audio.speaking")}
              </span>
            </div>
          )}
        </div>
      )}

      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group ${
          showSpeakingPulse
            ? "bg-cyan-500/90 hover:bg-cyan-500 text-white"
            : "bg-primary/90 hover:bg-primary text-primary-foreground"
        }`}
        title={t("audio.audioControls")}
        style={{
          boxShadow: showSpeakingPulse
            ? "0 0 20px rgba(34,211,238,0.4)"
            : "0 0 20px rgba(var(--glow-rgb), 0.3)",
        }}
      >
        {isSpeaking ? (
          <div className="flex items-center gap-0.5">
            {[0, 1, 2].map(i => (
              <div key={i} className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: `${6 + i * 4}px`, animationDelay: `${i * 150}ms` }} />
            ))}
          </div>
        ) : (
          <MoreVertical className="w-5 h-5 group-hover:scale-110 transition-transform" />
        )}
      </button>
    </div>
  );
}
